/**
 * AI Pipeline Admin API client — domain-specific functions for the
 * AI pipeline administration dashboard.
 *
 * Requirements: 8.1, 8.2, 8.3, 8.4, 8.5, 8.6, 8.7
 */

import * as api from '../services/api-client';
import type {
  PipelineStatusResponse,
  DlqResponse,
  PipelineConfigResponse,
  PipelineMetricsResponse,
  PipelineHealthResponse,
  RetriggerResponse,
} from '../types/ai-pipeline';

// ---------------------------------------------------------------------------
// Response wrappers (BFF returns SingleResponse<T> = { data: T })
// ---------------------------------------------------------------------------

interface SingleResponse<T> {
  readonly data: T;
}

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

/**
 * Fetches pipeline execution status: counts, recent executions, processing rate.
 */
export async function getPipelineStatus(): Promise<PipelineStatusResponse> {
  const response = await api.get<SingleResponse<PipelineStatusResponse>>(
    '/v1/admin/ai-pipeline/status'
  );
  return response.data;
}

/**
 * Fetches DLQ message count and recent messages.
 */
export async function getPipelineDlq(): Promise<DlqResponse> {
  const response = await api.get<SingleResponse<DlqResponse>>(
    '/v1/admin/ai-pipeline/dlq'
  );
  return response.data;
}

/**
 * Purges all messages from the AI pipeline DLQ.
 */
export async function purgePipelineDlq(): Promise<void> {
  await api.del('/v1/admin/ai-pipeline/dlq');
}

/**
 * Fetches pipeline configuration: model IDs and pipeline version.
 */
export async function getPipelineConfig(): Promise<PipelineConfigResponse> {
  const response = await api.get<SingleResponse<PipelineConfigResponse>>(
    '/v1/admin/ai-pipeline/config'
  );
  return response.data;
}

/**
 * Fetches pipeline metrics: knowledge units, vectors, and relationship edges.
 */
export async function getPipelineMetrics(): Promise<PipelineMetricsResponse> {
  const response = await api.get<SingleResponse<PipelineMetricsResponse>>(
    '/v1/admin/ai-pipeline/metrics'
  );
  return response.data;
}

/**
 * Fetches pipeline health: CloudWatch alarm states and aggregate health.
 */
export async function getPipelineHealth(): Promise<PipelineHealthResponse> {
  const response = await api.get<SingleResponse<PipelineHealthResponse>>(
    '/v1/admin/ai-pipeline/health'
  );
  return response.data;
}

/**
 * Triggers the AI pipeline for a specific page.
 *
 * @param pageId - The page to re-process through the pipeline
 */
export async function retriggerPipeline(pageId: string): Promise<RetriggerResponse> {
  const response = await api.post<SingleResponse<RetriggerResponse>>(
    '/v1/admin/ai-pipeline/retrigger',
    { pageId }
  );
  return response.data;
}
